import Image from "next/image";

import CloseCircleIcon from "@repo/ui/assets/flight/close_circle_icon.svg";
import AmericanAirlineIcon from "@repo/ui/assets/icons/american-airline.png";
import JetsmartAgenciesIcon from "@repo/ui/assets/icons/Jetsmart-agencies.png";
import JetsmartCompaniesIcon from "@repo/ui/assets/icons/Jetsmart-companies.png";
import PersonIcon from "@repo/ui/assets/icons/person.svg";
import { EnvLink } from "@repo/ui/components/EnvLink";
import { Card, CardContent } from "@repo/ui/components/ui/card";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@repo/ui/components/ui/dialog";
import { Link } from "@repo/ui/navigation";
import type { HomepageLoginModal } from "@repo/ui/types/cms/common";

interface LoginProps {
  openControlled: boolean;
  homepageLoginModal: HomepageLoginModal | null;
  setOpenControlled: (open: boolean) => void;
}

export default function LoginDialog({
  openControlled,
  homepageLoginModal,
  setOpenControlled,
}: Readonly<LoginProps>) {
  return (
    <Dialog open={openControlled} onOpenChange={setOpenControlled} data-testid="login-dialog-modal">
      <DialogContent
        className="data-[state=open]:zoom-in-100 data-[state=open]:slide-in-from-right data-[state=closed]:zoom-out-100 data-[state=closed]:slide-out-to-right overflow-y-auto rounded-none p-[32px] bg-white duration-500 [&>button:last-child]:hidden max-w-[100%] md:h-[550] h-[100%] gap-[32px]
                        fixed md:top-[345px] left-auto right-0 translate-x-0 md:translate-x-0"
        onPointerDownOutside={(e) => e.preventDefault()}
        data-testid="login-dialog-content"
      >
        <DialogHeader
          className="flex flex-row items-center justify-between border-b-1"
          data-testid="login-dialog-header"
        >
          <DialogTitle
            className="text-[#1B365D] font-bold md:text-xl text-[20px] mb-2"
            data-testid="login-dialog-title-lbl"
          >
            {homepageLoginModal?.accessAccountHeading || "Access your account as:"}
          </DialogTitle>
          <DialogClose asChild>
            <button
              className="cursor-pointer -mt-10 -mr-4"
              aria-label="Close modal"
              data-testid="login-dialog-close-btn"
            >
              <Image
                src={CloseCircleIcon}
                alt="Close icon"
                width={30}
                height={30}
                priority
                className="transition duration-200 hover:brightness-75"
              />
            </button>
          </DialogClose>
        </DialogHeader>
        <DialogDescription className="sr-only">Login Dialog</DialogDescription>

        <div
          className="grid grid-cols-2 gap-2 md:gap-4 text-center text-[#1B365D] font-medium sm:text-sm md:-mb-6 -mb-12"
          data-testid="login-dialog-options-section"
        >
          <Link href="/login" data-testid="login-person-link">
            <Card
              onClick={() => {
                setOpenControlled(false);
              }}
              className="border-1 border-black-70 rounded-sm shadow-none py-[12px] w-full hover:bg-gray-100 cursor-pointer h-[100]"
              data-testid="login-person-card"
            >
              <CardContent className="px-0" data-testid="login-person-card-content">
                <Image
                  src={homepageLoginModal?.personIcon || PersonIcon}
                  alt="Person"
                  width={40}
                  height={40}
                  className="w-[40px] m-auto"
                  data-testid="login-person-icon"
                />
                <p className="pt-4 text-[14px]" data-testid="login-person-lbl">
                  {homepageLoginModal?.personLabel || "Person"}
                </p>
              </CardContent>
            </Card>
          </Link>
          {/* <EnvLink
            hrefs={{
              default:
                "https://booking.betauat.dgital.com/V2/Login?bancoe=1&culture=es-cl&url=https://betauat.dgital.com/cl/es/",
              prod: "https://booking.jetsmart.com/V2/Login?bancoe=1&culture=es-cl&url=https://jetsmart.com/cl/es/",
            }}
            data-testid="login-banco-link"
          >
            <Card
              className="border-1 border-black-70 rounded-sm shadow-none py-[12px] w-full hover:bg-gray-100 cursor-pointer h-[100]"
              data-testid="login-banco-card"
            >
              <CardContent className="px-0" data-testid="login-banco-card-content">
                <Image
                  src={homepageLoginModal?.stateBankIcon || BancoEstadoIcon}
                  alt="State Bank"
                  width={40}
                  height={40}
                  className="m-auto w-[40px]"
                  data-testid="login-banco-icon"
                />
                <p className="pt-4 text-[14px] md:text-sm" data-testid="login-banco-lbl">
                  {homepageLoginModal?.stateBankLabel || "State Bank"}
                </p>
              </CardContent>
            </Card>
          </EnvLink> */}

          <EnvLink
            hrefs={{
              default:
                "https://nonprod-login.aa.com/login?flowId=CDL44OR5v9&qmUser=undefined&qmSession=undefined&locale=es_CL",
              prod: "https://login.aa.com/login?flowId=ZC0cC9OixG&qmUser=undefined&qmSession=undefined&locale=es_CL",
            }}
            data-testid="login-aa-link"
          >
            <Card
              className="border-1 border-black-70 rounded-sm shadow-none py-[12px] w-full hover:bg-gray-100 cursor-pointer h-[100]"
              data-testid="login-aa-card"
            >
              <CardContent className="px-1" data-testid="login-aa-card-content">
                <Image
                  src={homepageLoginModal?.americanAirlinesIcon || AmericanAirlineIcon}
                  alt="American Airlines"
                  width={110}
                  height={26}
                  className="m-auto h-[26px] object-contain"
                  data-testid="login-aa-icon"
                />
                <p className="pt-2 text-[14px] md:text-sm" data-testid="login-aa-lbl">
                  {homepageLoginModal?.americanAirlinesLabel || "AAdvantage® program"}
                </p>
              </CardContent>
            </Card>
          </EnvLink>
        </div>
        <h6
          className="font-bold md:text-xl text-[20px] text-[#1B365D] pb-0 border-b-1 border-black-60 mt-3 h-10 md:-mb-4 -mb-12"
          data-testid="login-dialog-company-heading-lbl"
        >
          {homepageLoginModal?.accessAsCompanyHeading || "Or access as a company or agency:"}
        </h6>

        <div
          className="grid grid-cols-2 gap-4 md:gap-6 mt-2 md:-mb-6 -mb-12"
          data-testid="login-dialog-company-section"
        >
          <EnvLink
            hrefs={{
              default:
                "https://booking.betauat.dgital.com/V2/Login?company=1&culture=es-cl&url=https://betauat.dgital.com/cl/es/",
              prod: "https://booking.jetsmart.com/V2/Login?company=1&culture=es-cl&url=https://jetsmart.com/cl/es/",
            }}
            data-testid="login-company-link"
          >
            <Card
              className="border-1 border-black-70 rounded-sm shadow-none h-[80] w-full hover:bg-gray-100 items-center"
              data-testid="login-company-card"
            >
              <Image
                src={homepageLoginModal?.jetsmartCompaniesIcon || JetsmartCompaniesIcon}
                alt="JetSmart Companies"
                width={100}
                height={80}
                className="w-25 h-auto m-auto"
                data-testid="login-company-icon"
              />
            </Card>
          </EnvLink>
          <EnvLink
            hrefs={{
              default:
                "https://booking.betauat.dgital.com/V2/Login?agency=1&culture=es-cl&url=https://betauat.dgital.com/cl/es/",
              prod: "https://booking.jetsmart.com/V2/Login?agency=1&culture=es-cl&url=https://jetsmart.com/cl/es/",
            }}
            data-testid="login-agency-link"
          >
            <Card
              className="border-1 border-black-70 rounded-sm shadow-none w-full h-[80] hover:bg-gray-100"
              data-testid="login-agency-card"
            >
              <Image
                src={homepageLoginModal?.jetsmartAgenciesIcon || JetsmartAgenciesIcon}
                alt="JetSmart Agencies"
                width={100}
                height={80}
                className="w-25 h-auto m-auto"
                data-testid="login-agency-icon"
              />
            </Card>
          </EnvLink>
        </div>

        <h6
          className="font-bold text-[20px] text-[#1B365D] pb-2 border-b-1 border-black-70 mt-3 h-10 md:-mb-2 -mb-10"
          data-testid="login-dialog-signup-heading-lbl"
        >
          {homepageLoginModal?.dontHaveAccountHeading || "Don't you have an account?"}
        </h6>

        <div
          className="text-center grid grid-cols-2 gap-4"
          data-testid="login-dialog-signup-section"
        >
          <Link
            href="/register"
            onClick={() => {
              setOpenControlled(false);
            }}
            className="text-[14px] flex items-center justify-center cursor-pointer font-medium h-[80] text-[#1B365D] border-1 border-black-70 rounded-sm p-[12px] hover:bg-gray-100"
            data-testid="login-signup-btn"
          >
            {homepageLoginModal?.signUpButtonLabel || "Sign up here"}
          </Link>
          {/* <EnvLink
            hrefs={{
              prod: "https://booking.jetsmart.com/V2/Login?bancoe=1&culture=es-cl&url=https://jetsmart.com/cl/es/&_gl=1*1r5div6*_gcl_au*MTMxODkwMzU3OC4xNzU0NTgyMjYxLjMwMjc1MjEyLjE3NTQ2ODQ4MjkuMTc1NDY4NDg0OQ",
            }}
            data-testid="login-register-banco-link"
          >
            <div
              className="text-[14px] font-medium h-[80] flex items-center justify-center text-[#1B365D] border-1 border-black-70 rounded-sm px-[12px] hover:bg-gray-100"
              data-testid="login-register-banco-section"
            >
              Register here BancoEstado Clients
            </div>
          </EnvLink> */}
          <EnvLink
            hrefs={{
              default:
                "https://www.aa.com/web/loyalty/profile/enrollment?locale=es_CL&c=DSP%7C%7C20240903%7CMKT%7CMCLAOTH%7CLNK%7C%7CMCLA_LYLTY_TRAN_JA_AADV_ENROLLMENT",
              prod: "https://www.aa.com/web/loyalty/profile/enrollment?locale=es_CL&c=DSP%7C%7C20240903%7CMKT%7CMCLAOTH%7CLNK%7C%7CMCLA_LYLTY_TRAN_JA_AADV_ENROLLMENT",
            }}
            data-testid="login-register-aa-link"
          >
            <div
              className="text-[14px] font-medium h-[80] flex items-center justify-center text-[#1B365D] border-1 border-black-70 rounded-sm px-[12px] hover:bg-gray-100"
              data-testid="login-register-aa-section"
            >
              Register here AAdvantage® Program
            </div>
          </EnvLink>
        </div>
      </DialogContent>
    </Dialog>
  );
}
